import { t } from "i18next";
import { ReactElement, useCallback, useMemo, useState } from "react";

import { ICatalogue } from "../../../apis/useGetCatalogueData/types";
import { globalEvents } from "../../../common/helpers/GlobalEvents";
import { isItemOrderLimitExceeded } from "../../../common/helpers/itemOrderLimitHelper";
import { centsToDollarsDisplay } from "../../../common/helpers/stringUtils";
import { useItemSubtotal } from "../../../common/hooks/useItemSubtotal";
import Condition from "../../../components/Conditional/Condition";
import I18nText from "../../../components/I18nText/I18nText";
import QuantitySelector from "../../../components/elements/Selection/quantity/QuantitySelector";
import { useCartContext } from "../../../contexts/CartServiceContext/CartServiceContext";
import { useConfigContext } from "../../../contexts/ConfigContext/ConfigContext";
import { useHandleClickProduct } from "../../ProductListPage/ProductSection/hooks";
import { PAGE_PARAM } from "../../constants";
import { CART_PAGE_EVENT, MAX_CART_ITEM_QUANTITY } from "../constants";
import CartItemCustomizations from "./CartItemCustomizations";
import CartItemSet from "./CartItemSet";
import { useCartItemQuantity } from "./useCartItemQuantity";

interface IProps {
	item: ICatalogue;
	idx: number;
}

const CartItem = ({ item, idx }: IProps): ReactElement => {
	const { updateQuantity } = useCartItemQuantity(idx);
	const {
		state: { cart, is_takeaway },
	} = useCartContext((state) => state);
	const { state: bcrs_deposit_rate } = useConfigContext((state) => state.config.bcrs_deposit_rate);
	const { state: itemLimitPerPax } = useConfigContext((state) => state.config.item_limit_per_pax);
	const { state: pax } = useConfigContext((state) => state.qrSession?.pax);
	const [revertKey, setRevertKey] = useState(0);

	const handleQuantityChange = useCallback(
		(qty: number) => {
			if (qty > item.quantity) {
				const proposed: ICatalogue = { ...item, quantity: qty };
				const cartWithoutThis = (cart ?? []).filter((_, i) => i !== idx);

				if (isItemOrderLimitExceeded(proposed, cartWithoutThis, itemLimitPerPax, pax ?? 0)) {
					globalEvents.emit(CART_PAGE_EVENT.SHOW_ITEM_LIMIT_MODAL);
					setRevertKey((k) => k + 1);

					return;
				}
			}

			updateQuantity(qty);
		},
		[item, cart, idx, itemLimitPerPax, pax, updateQuantity]
	);
	const subtotal = useItemSubtotal([item]);
	const handleItemClick = useHandleClickProduct();

	const handleClickTitle = handleItemClick(
		item.brand_menu_item_id,
		PAGE_PARAM.EDIT,
		idx.toString()
	);

	const hasCustomizations = useMemo(
		() =>
			item.food_supplier_customizations?.some((c) =>
				Boolean(c.food_supplier_customization_options?.length)
			),
		[item.food_supplier_customizations]
	);

	const bcrsDepositAmount = useMemo(() => {
		return item.quantity * item.bcrs * bcrs_deposit_rate;
	}, [item.quantity, item.bcrs, bcrs_deposit_rate]);

	const bcrsDepositText = useMemo(() => {
		const totalBcrsCount = item.quantity * item.bcrs;

		if (totalBcrsCount > 1) {
			return `${t("cartPage.bcrs.deposit")} × ${totalBcrsCount}`;
		}

		return t("cartPage.bcrs.deposit");
	}, [item.quantity, item.bcrs, t]);

	return (
		<li className="CartItem" key={`CartItem-${item.id}-${item.brand_menu_item_id}`}>
			<div className="ItemHeader">
				<button
					className="ItemName"
					data-testid={`item-name-${item.brand_menu_item_id}`}
					onClick={handleClickTitle}
				>
					<I18nText className="ItemNameText" enText={item.title} cnText={item.title_cn} />
				</button>
				<div className="ItemHeaderRight">
					<span className="Quantity">
						<QuantitySelector
							key={`qs-${idx}-${revertKey}`}
							max={MAX_CART_ITEM_QUANTITY}
							onChange={handleQuantityChange}
							showZero={true}
							initValue={item.quantity}
						/>
					</span>
					<span className="Price TabularNums">{`$${centsToDollarsDisplay(subtotal)}`}</span>
				</div>
			</div>
			<div className="ItemContents">
				<Condition check={hasCustomizations}>
					<CartItemCustomizations customizations={item.food_supplier_customizations ?? []} />
				</Condition>
				<Condition check={Boolean(item.food_supplier_set_items?.length)}>
					<CartItemSet
						setitems={item.food_supplier_set_items ?? []}
						parentQuantity={item.quantity}
					/>
				</Condition>
				<Condition check={item.bcrs > 0}>
					<ul className="CartItemBcrsDeposit">
						<li className="BcrsDeposit">
							<div className="BcrsDepositTitle">{bcrsDepositText}</div>
							<span className="BcrsDepositValue TabularNums">
								+ ${centsToDollarsDisplay(bcrsDepositAmount)}
							</span>
						</li>
					</ul>
				</Condition>
				<Condition check={is_takeaway && item.takeaway_fee !== 0}>
					<ul className="CartItemTakeawayFee">
						<li className="TakeawayFee">
							<div className="TakeawayFeeTitle">{t("cartPage.item.takeawayfee")}</div>
							<span className="TakeawayFeeValue TabularNums">
								+ ${centsToDollarsDisplay(item.takeaway_fee)}
							</span>
						</li>
					</ul>
				</Condition>
			</div>
		</li>
	);
};

export default CartItem;
